import { supabase } from './supabase';

// Gym Management Service for System Administrators
export const gymManagementService = {
  // Get all gyms with member counts
  async getAllGyms() {
    try {
      const { data: gyms, error } = await supabase?.from('gyms')?.select(`
          *,
          members:user_profiles(count)
        `)?.order('created_at', { ascending: false });

      if (error) throw error;

      // Transform data to include member count
      const gymsWithCounts = gyms?.map(gym => ({
        ...gym,
        memberCount: gym?.members?.[0]?.count || 0
      }));

      return { data: gymsWithCounts, error: null };
    } catch (error) {
      console.error('Error fetching gyms:', error);
      return { data: null, error };
    }
  },

  // Get single gym by ID
  async getGymById(gymId) {
    try {
      const { data: gym, error } = await supabase?.from('gyms')?.select(`
          *,
          members:user_profiles(count)
        `)?.eq('id', gymId)?.single();

      if (error) throw error;

      return {
        data: {
          ...gym,
          memberCount: gym?.members?.[0]?.count || 0
        },
        error: null
      };
    } catch (error) {
      console.error('Error fetching gym:', error);
      return { data: null, error };
    }
  },

  // Create new gym
  async createGym(gymData) {
    try {
      const { data: gym, error } = await supabase?.from('gyms')?.insert([{
          name: gymData?.name,
          address: gymData?.address,
          city: gymData?.city,
          state: gymData?.state,
          zip_code: gymData?.zipCode,
          phone: gymData?.phone,
          email: gymData?.email,
          is_active: gymData?.isActive ?? true
        }])?.select()?.single();

      if (error) throw error;
      return { data: gym, error: null };
    } catch (error) {
      console.error('Error creating gym:', error);
      return { data: null, error };
    }
  },

  // Update gym
  async updateGym(gymId, gymData) {
    try {
      const updateData = {};
      if (gymData?.name !== undefined) updateData.name = gymData?.name;
      if (gymData?.address !== undefined) updateData.address = gymData?.address;
      if (gymData?.city !== undefined) updateData.city = gymData?.city;
      if (gymData?.state !== undefined) updateData.state = gymData?.state;
      if (gymData?.zipCode !== undefined) updateData.zip_code = gymData?.zipCode;
      if (gymData?.phone !== undefined) updateData.phone = gymData?.phone;
      if (gymData?.email !== undefined) updateData.email = gymData?.email;
      if (gymData?.isActive !== undefined) updateData.is_active = gymData?.isActive;
      updateData.updated_at = new Date()?.toISOString();

      const { data: gym, error } = await supabase?.from('gyms')?.update(updateData)?.eq('id', gymId)?.select()?.single();

      if (error) throw error;
      return { data: gym, error: null };
    } catch (error) {
      console.error('Error updating gym:', error);
      return { data: null, error };
    }
  },

  // Toggle gym active status
  async toggleGymStatus(gymId, isActive) {
    try {
      const { data: gym, error } = await supabase?.from('gyms')?.update({ 
          is_active: isActive,
          updated_at: new Date()?.toISOString()
        })?.eq('id', gymId)?.select()?.single();

      if (error) throw error;
      return { data: gym, error: null };
    } catch (error) {
      console.error('Error toggling gym status:', error);
      return { data: null, error };
    }
  },

  // Delete gym (soft delete by setting is_active to false)
  async deleteGym(gymId) {
    try {
      const { data: gym, error } = await supabase?.from('gyms')?.update({ 
          is_active: false,
          updated_at: new Date()?.toISOString()
        })?.eq('id', gymId)?.select()?.single();

      if (error) throw error;
      return { data: gym, error: null };
    } catch (error) {
      console.error('Error deleting gym:', error);
      return { data: null, error };
    }
  },

  // Get gym statistics
  async getGymStatistics() {
    try {
      const { data: gyms, error } = await supabase?.from('gyms')?.select('id, is_active');

      if (error) throw error;

      const totalGyms = gyms?.length || 0;
      const activeGyms = gyms?.filter(g => g?.is_active)?.length || 0;
      const inactiveGyms = totalGyms - activeGyms;

      return {
        data: {
          totalGyms,
          activeGyms,
          inactiveGyms
        },
        error: null
      };
    } catch (error) {
      console.error('Error fetching gym statistics:', error);
      return { data: null, error };
    }
  }
};
